import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  FileText, Download, Users, Student, CurrencyDollar,
  Receipt, UserMinus, ChartBar, Spinner, Info
} from '@phosphor-icons/react';

const REPORT_TYPES = [
  {
    value: 'donors', label: 'Donor Report',
    icon: Users,
    desc: 'Complete donor directory with donation totals, category breakdown, top contributors, and paired student details.',
    sections: ['KPI Summary', 'By Category', 'By Type', 'Top 20 Donors', 'Full Directory', 'Donation Transactions']
  },
  {
    value: 'recipients', label: 'Recipient Report',
    icon: Student,
    desc: 'All recipients with financial status, school breakdown, funding gap analysis, and balance overview.',
    sections: ['KPI Summary', 'Financial Overview', 'By School', 'By Status', 'Funding Gap Analysis', 'Full Directory']
  },
  {
    value: 'financial', label: 'Financial Report',
    icon: CurrencyDollar,
    desc: 'Complete financial statement with fund balances, transaction ledger, monthly trends, and type breakdown.',
    sections: ['KPI Summary', 'Fund Balances', 'Transaction Types', 'Monthly Trend', 'Full Transaction Ledger']
  },
  {
    value: 'expenses', label: 'Expense Report',
    icon: Receipt,
    desc: 'All expenses with approval status, category analysis, vendor breakdown, and monthly trend.',
    sections: ['KPI Summary', 'By Category', 'By Vendor', 'Monthly Trend', 'Complete Expense Register']
  },
  {
    value: 'dropouts', label: 'Dropout Report',
    icon: UserMinus,
    desc: 'Dropout records with fund transfers, destination breakdown, reasons analysis, and fund ledger.',
    sections: ['KPI Summary', 'Fund Balances', 'By Destination', 'By Reason', 'Full Register', 'Fund Ledger']
  },
  {
    value: 'comprehensive', label: 'Comprehensive Report',
    icon: ChartBar,
    desc: 'Full system overview combining all metrics — donations, expenses, recipients, funds, dropouts, and trends.',
    sections: ['Executive Summary', 'Key Metrics', 'Fund Balances', 'Yearly Donations', 'Expenses by Category', 'By School', 'Dropout Summary', 'Top Donors', 'Top Students', 'Recent Transactions']
  },
];

export default function ReportsHub() {
  const [reportType, setReportType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [generating, setGenerating] = useState(false);

  const selectedReport = REPORT_TYPES.find(r => r.value === reportType);

  const generateReport = async () => {
    if (!reportType) { toast.error('Select a report type'); return; }
    setGenerating(true);
    try {
      const response = await api.post('/reports/generate',
        { report_type: reportType, date_from: dateFrom, date_to: dateTo },
        { responseType: 'blob', timeout: 120000 }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `donex_${reportType}_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report generated and downloaded');
    } catch (e) {
      console.error('Report generation failed:', e);
      toast.error('Failed to generate report. Please try again.');
    }
    setGenerating(false);
  };

  return (
    <div className="space-y-6" data-testid="reports-hub">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 font-['Chivo']">Reports</h2>
        <span className="text-xs text-slate-400">Select a report type below to generate a detailed PDF</span>
      </div>

      {/* Report Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {REPORT_TYPES.map(rt => {
          const Icon = rt.icon;
          const isSelected = reportType === rt.value;
          return (
            <Card key={rt.value}
              className={`bg-white border rounded-sm shadow-none cursor-pointer transition-all duration-150 ${isSelected ? 'border-[#0F6CBD] ring-1 ring-[#0F6CBD]/20 bg-blue-50/30' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}`}
              onClick={() => setReportType(rt.value)}
              data-testid={`report-type-${rt.value}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded ${isSelected ? 'bg-[#0F6CBD]/10' : 'bg-slate-50'}`}>
                    <Icon size={20} weight={isSelected ? 'fill' : 'regular'}
                      className={isSelected ? 'text-[#0F6CBD]' : 'text-slate-400'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm font-medium ${isSelected ? 'text-[#0F6CBD]' : 'text-slate-800'}`}>{rt.label}</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{rt.desc}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters & Generate */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Date From</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="rounded-sm w-44 text-sm" data-testid="report-date-from" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Date To</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="rounded-sm w-44 text-sm" data-testid="report-date-to" />
            </div>
            <div className="flex items-center gap-2">
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="text-xs text-slate-500 hover:text-slate-700" data-testid="clear-dates-btn">
                  Clear dates
                </Button>
              )}
              <Button onClick={generateReport} disabled={generating || !reportType}
                className="bg-[#0F6CBD] hover:bg-[#005A9E] text-white rounded-sm text-sm h-9 px-5"
                data-testid="generate-report-btn">
                {generating ? (
                  <><Spinner size={16} className="mr-2 animate-spin" />Generating...</>
                ) : (
                  <><Download size={16} className="mr-2" />Generate PDF</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Preview / Info */}
      {selectedReport ? (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none" data-testid="report-preview">
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-4">
              <Info size={18} className="text-[#0F6CBD] mt-0.5 shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-slate-800">{selectedReport.label} — Included Sections</h3>
                <p className="text-xs text-slate-500 mt-1">
                  {dateFrom || dateTo
                    ? `Filtered: ${dateFrom || 'start'} to ${dateTo || 'present'}`
                    : 'All time data (use date filters above to narrow the range)'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedReport.sections.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-50 border border-slate-100 rounded text-xs text-slate-600">
                  <FileText size={12} className="text-slate-400" />{s}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="report-placeholder">
          <FileText size={48} className="text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Select a report type above to see what's included.</p>
          <p className="text-xs text-slate-400 mt-1">Reports are generated as detailed PDF documents with multiple sections, summary statistics, and data tables.</p>
        </div>
      )}
    </div>
  );
}
